/**
 * PluQuestionPanel — free-form Q&A on a fiche's PLU.
 *
 * UX :
 *   • Header with section number + intro.
 *   • Suggested-prompts chips (the agent doesn't always know what to
 *     ask Claude — quick examples lower the activation barrier).
 *   • Conversation list (oldest first) — each card has the question,
 *     the answer, a confidence pill, and the citations as inline
 *     "i" badges that re-use the SourceBadge popover infra.
 *   • Composer at the bottom with textarea + send button. Disabled
 *     while a request is in flight.
 *   • Quota banner when the agent has consumed all 20 questions.
 *
 * Cost reminder shown discreetly under the composer (~0,02 €/question)
 * — not to discourage use, but to be transparent about the COGS.
 */

import { useState, useRef, useEffect } from "react";
import { Card, Pill, Button } from "./ui";
import { Ic } from "./icons";
import { askPluQuestion, usePluQuestions } from "../lib/pluQuestions";
import type { PluQuestion } from "../lib/types";

const MAX_QUESTION_CHARS = 500;
const QUOTA = 20;

const SUGGESTIONS = [
  "Puis-je faire une piscine ?",
  "Quelle couleur de toiture est autorisée ?",
  "Y a-t-il une obligation de stationnement vélo ?",
  "Quelle hauteur pour une extension ?",
  "Quelles essences végétales sont recommandées ?",
];

export function PluQuestionPanel({ ficheId }: { ficheId: string }) {
  const { questions, loading } = usePluQuestions(ficheId);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const conversationEndRef = useRef<HTMLDivElement>(null);

  // Scroll the freshest answer into view as it arrives — the conversation
  // can grow quickly (up to 20 turns) and the composer is sticky at the
  // bottom of the section, so without auto-scroll the new answer can
  // appear below the fold.
  useEffect(() => {
    if (questions.length === 0) return;
    conversationEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [questions.length]);

  const remaining = Math.max(0, QUOTA - questions.length);
  const atQuota = remaining === 0;

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    const q = input.trim();
    if (!q || pending || atQuota) return;
    setPending(true);
    setError(null);
    try {
      await askPluQuestion({ ficheId, question: q });
      setInput("");
      // Refocus the textarea so the agent can ask follow-ups without
      // breaking flow (mouse → keyboard → mouse round-trips kill UX).
      textareaRef.current?.focus();
    } catch (e) {
      const code = (e as { details?: { code?: string }; code?: string })
        ?.details?.code ?? (e as { code?: string })?.code;
      const message =
        code === "plu-question-rate-limited"
          ? `Vous avez atteint le quota de ${QUOTA} questions pour cette fiche.`
          : (e as { message?: string })?.message ??
            "Erreur réseau. Réessayez dans un instant.";
      setError(message);
    } finally {
      setPending(false);
    }
  }

  function pickSuggestion(s: string) {
    setInput(s);
    textareaRef.current?.focus();
  }

  return (
    <section className="mt-12">
      <div className="text-[11px] tracking-[0.14em] uppercase text-ink-500 font-medium">
        04 · Posez vos questions au PLU
      </div>
      <h2 className="mt-2 text-[24px] sm:text-[28px] font-semibold tracking-tight">
        L'IA répond, sur la base du règlement extrait.
      </h2>
      <p className="mt-2 text-[13.5px] text-ink-600 leading-relaxed max-w-[680px]">
        Demandez tout ce que le tableau ci-dessus ne couvre pas — matériaux,
        clôtures, vélos, panneaux solaires, antennes… Les réponses citent
        l'article exact du règlement, comme dans le tableau.
      </p>

      {/* Conversation */}
      {loading ? (
        <div className="mt-6 text-[13px] text-ink-500">Chargement…</div>
      ) : questions.length === 0 ? (
        <Card className="mt-6 p-5 bg-ink-50/60">
          <div className="text-[13px] text-ink-700 mb-3">
            Quelques exemples pour démarrer :
          </div>
          <div className="flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => pickSuggestion(s)}
                className="text-[12.5px] bg-paper hairline rounded-full px-3 py-1.5 hover:bg-ink-100 transition"
              >
                {s}
              </button>
            ))}
          </div>
        </Card>
      ) : (
        <div className="mt-6 space-y-4">
          {questions.map((q) => (
            <QuestionCard key={q.id} q={q} />
          ))}
          <div ref={conversationEndRef} />
        </div>
      )}

      {/* Composer */}
      <form onSubmit={handleSubmit} className="mt-6">
        <Card className="p-4">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) =>
              setInput(e.target.value.slice(0, MAX_QUESTION_CHARS))
            }
            onKeyDown={(e) => {
              // Cmd/Ctrl + Enter submits — saves a mouse trip for power users.
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                void handleSubmit();
              }
            }}
            disabled={atQuota || pending}
            placeholder={
              atQuota
                ? "Quota atteint pour cette fiche."
                : "Tapez votre question sur le règlement…"
            }
            rows={2}
            className="w-full bg-transparent outline-none resize-none text-[14px] placeholder:text-ink-400 disabled:opacity-50"
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <div className="text-[11px] text-ink-500 flex items-center gap-3">
              <span>
                {input.length}/{MAX_QUESTION_CHARS}
              </span>
              <span className="text-ink-400">·</span>
              <span>
                {remaining}/{QUOTA} question{remaining > 1 ? "s" : ""}{" "}
                restante{remaining > 1 ? "s" : ""}
              </span>
              <span className="text-ink-400">·</span>
              <span title="Coût Claude approximatif par question">~0,02 €</span>
            </div>
            <Button
              type="submit"
              variant="dark"
              size="sm"
              disabled={!input.trim() || pending || atQuota}
              icon={
                pending ? (
                  <span className="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
                ) : (
                  <Ic.Bolt size={12} />
                )
              }
            >
              {pending ? "Analyse en cours…" : "Envoyer"}
            </Button>
          </div>
        </Card>
        {error && (
          <div className="mt-2 text-[12.5px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            ⚠ {error}
          </div>
        )}
      </form>
    </section>
  );
}

// ─── One Q&A card ────────────────────────────────────────────────────────
function QuestionCard({ q }: { q: PluQuestion }) {
  const confidenceTone =
    q.confidence === "high"
      ? "green"
      : q.confidence === "medium"
        ? "amber"
        : "soft";
  const confidenceLabel =
    q.confidence === "high"
      ? "Confiance élevée"
      : q.confidence === "medium"
        ? "Confiance modérée"
        : "À vérifier";

  return (
    <Card className="p-5">
      {/* Question */}
      <div className="flex items-start gap-3">
        <span className="shrink-0 w-6 h-6 rounded-full bg-ink-900 text-white grid place-items-center text-[11px] font-semibold">
          ?
        </span>
        <div className="text-[14px] font-medium text-ink-900 leading-relaxed flex-1 min-w-0">
          {q.question}
        </div>
      </div>

      {/* Answer */}
      <div className="mt-4 ml-9 text-[13.5px] text-ink-800 leading-relaxed whitespace-pre-wrap">
        {q.answer}
      </div>

      {/* Footer : citations + confidence */}
      <div className="mt-4 ml-9 flex items-start justify-between gap-3 flex-wrap pt-3 border-t border-ink-200/70">
        <div className="flex flex-wrap gap-1.5">
          {q.citations.map((c, i) => (
            <CitationChip key={i} citation={c} />
          ))}
        </div>
        <Pill tone={confidenceTone} icon={<Ic.Check size={11} />}>
          {confidenceLabel}
        </Pill>
      </div>
    </Card>
  );
}

// Reusable citation chip — opens a popover with the verbatim PLU excerpt.
// Lighter weight than the SourceBadge in Fiche.tsx (no portal needed since
// the panel doesn't sit inside a clipping container).
function CitationChip({
  citation,
}: {
  citation: PluQuestion["citations"][number];
}) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-[11.5px] bg-ink-100 hover:bg-ink-200 text-ink-700 rounded-full px-2.5 py-1 transition"
      >
        <Ic.Bolt size={10} />
        <span>{citation.article ?? "Source"}</span>
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute z-50 bottom-full left-0 mb-2 w-[320px] bg-ink-900 text-white rounded-lg shadow-pop p-3.5 text-[12px] leading-relaxed normal-case tracking-normal text-left font-normal"
        >
          {citation.article && (
            <div className="text-[10.5px] font-mono uppercase tracking-widest text-lime-300 mb-1.5">
              {citation.article}
            </div>
          )}
          <div className="italic text-white/95">« {citation.excerpt} »</div>
        </div>
      )}
    </span>
  );
}
